import { InfoCircleOutlined } from '@ant-design/icons';
import TcModal from 'components/UI/Modal/TcModal';
import TcTab, { TabsProp } from 'components/UI/Tab/TcTab';
import TcCoverLoading from 'components/UI/TcLoading/TcCoverLoading';
import ReservedLockerContent from 'components/pages/dashboard/ParcelOrder/components/orderAddressPicker/compoonents/reservedLockerContent';
import endpointUrls from 'global/Constants/endpointUrls';
import { IBackendGetGeneric, IBackendSearchGeneric } from 'global/Models/globalModels';
import lockerModel, { pudoBranch, userAccessibleLKiosk } from 'global/Models/lockerModel';
import { lockerInfo, packageCreate } from 'global/Models/omOrderModel';
import { handleApiThenGeneric } from 'global/helperFunctions/handleApiThen';
import { localApiCatcher } from 'global/helperFunctions/localApiCatcher';
import ApiService, { errorResponse } from 'global/utils/ApiService';
import { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { setOmOrderReservedByLocker } from 'redux/reducer/ParcelOrder/parcelOrderReducer';
import { RootState, store } from 'redux/store';
import OrderSelectLockerListOnMap from './OrderSelectLockerListOnMapTab';
import OrderSelectLockerListTab from './OrderSelectLockerListTab';
import OrderSelectPudoListTab from './OrderSelectPudoListTab';

interface IProps {
  setLockerAddress?: (_locker: lockerInfo) => void;
  packages: packageCreate[] | undefined;
  index: number;
  visible?: number;
  close: () => void;
  lockerAddress?: lockerInfo;
}

const OrderSelectLocker: FC<IProps> = ({ packages, setLockerAddress, visible, close, lockerAddress, index }) => {
  //state
  const [errorMsg, setErrorMsg] = useState<string>();
  const [loading, setLoading] = useState<boolean>(false);
  const [pudoLoading, setPudoLoading] = useState<boolean>(false);
  const [tab, setTab] = useState<'kioskList' | 'kioskListOnMap' | 'pudoList'>('kioskListOnMap');
  const [userAccessibleKiosksList, setUserAccessibleKiosksList] = useState<IBackendGetGeneric<userAccessibleLKiosk>>();
  const [pudoBranchesList, setPudoBranchesList] = useState<pudoBranch[]>([]);
  const omOrderReservedByLocker = useSelector((state: RootState) => state.parcelOrder.omOrderReservedByLocker);

  //effect
  useEffect(() => {
    if (!visible) return;
    getUserAccessibleKiosks(visible);
    getPudoBranches(visible);
  }, [visible]);

  //functions - must be before tabs array
  const handleClose = () => {
    setTab('kioskListOnMap');
    setErrorMsg(undefined);
    store.dispatch(setOmOrderReservedByLocker(undefined));
    setUserAccessibleKiosksList(undefined);
    setPudoBranchesList([]);

    close();
  };

  const tabs: TabsProp[] = [];

  !lockerAddress &&
    userAccessibleKiosksList &&
    tabs.push({
      content: (
        <OrderSelectLockerListOnMap
          cityId={visible}
          packages={packages}
          loading={loading}
          userAccessibleKiosksList={userAccessibleKiosksList}
          pudoBranchesList={pudoBranchesList}
          setLockerAddress={setLockerAddress}
          index={index}
          handleClose={handleClose}
        />
      ),
      title: lockerModel.KioskListOnMapTitle,
      key: 'kioskListOnMap',
    });

  !lockerAddress &&
    userAccessibleKiosksList &&
    tabs.push({
      content: (
        <OrderSelectLockerListTab
          userAccessibleKiosksList={userAccessibleKiosksList}
          packages={packages}
          visible={visible}
          loading={loading}
          setLockerAddress={setLockerAddress}
          index={index}
          handleClose={handleClose}
        />
      ),
      title: lockerModel.KioskListTitle,
      key: 'kioskList',
    });

  !lockerAddress &&
    pudoBranchesList.length > 0 &&
    tabs.push({
      content: <OrderSelectPudoListTab pudoBranchesList={pudoBranchesList} loading={pudoLoading} setLockerAddress={setLockerAddress} cityId={visible} handleClose={handleClose} />,
      title: 'لیست پودوها',
      key: 'pudoList',
    });

  //functions
  const getUserAccessibleKiosks = async (visible: number) => {
    setLoading(true);
    const payload: any = {};
    if (packages?.length) {
      payload.lat = packages[0].receiverAddress?.address.latitude;
      payload.lon = packages[0].receiverAddress?.address.longitude;
    } else {
      payload.cityId = visible;
    }
    await ApiService.post(endpointUrls.lockerSearchBunch, payload)
      .then((res: IBackendGetGeneric<userAccessibleLKiosk>) =>
        handleApiThenGeneric<IBackendGetGeneric<userAccessibleLKiosk>, userAccessibleLKiosk>({
          res,
          onSuccess: setUserAccessibleKiosksList,
          notifFail: false,
          notifSuccess: false,
          onFailed: () => {
            setErrorMsg(res.message || 'خطا در دریافت اطلاعات');

            setUserAccessibleKiosksList(undefined);
          },
        })
      )
      .catch(() => localApiCatcher(errorResponse));
    setLoading(false);
  };

  const getPudoBranches = async (cityId: number) => {
    setPudoLoading(true);
    await ApiService.post(endpointUrls.getPudoBranches, { cityId })
      .then((res: IBackendSearchGeneric<pudoBranch>) =>
        handleApiThenGeneric<IBackendSearchGeneric<pudoBranch>, pudoBranch[]>({
          res,
          onSuccessData: (data) => {
            setPudoBranchesList(data || []);
          },
          notifFail: false,
          notifSuccess: false,
          onFailed: () => {
            setPudoBranchesList([]);
          },
        })
      )
      .catch(() => {
        localApiCatcher(errorResponse);
        setPudoBranchesList([]);
      });
    setPudoLoading(false);
  };

  const handleTabChange = (tab: 'kioskList' | 'kioskListOnMap' | 'pudoList') => setTab(tab);

  return (
    <TcModal title='انتخاب کیوسک جهت رزرو' className='h-4/5!' visible={!!visible} width={errorMsg ? 500 : 1000} onCancel={handleClose} footer={false}>
      {!omOrderReservedByLocker && !errorMsg && tab !== 'pudoList' && (
        <div className='flex items-center py-4 font-medium'>
          <InfoCircleOutlined className='ml-1 text-lg inherit-color text-t-error-color' />
          توجه کنید که تنها تا ۵ دقیقه پس از رزرو فرصت ثبت سفارش دارید و پس از آن امکان پرشدن کیوسک هست.
        </div>
      )}
      {(loading || pudoLoading) && !userAccessibleKiosksList && !pudoBranchesList.length && <TcCoverLoading text='درحال دریافت لیست کیوسک های در محدوده ی شما' />}
      {!lockerAddress && tabs.length > 0 && <TcTab tabs={tabs} onChange={handleTabChange} activeTab={tab} />}
      <ReservedLockerContent orderReservationData={omOrderReservedByLocker} />
      {errorMsg && <span>{errorMsg}</span>}
    </TcModal>
  );
};

export default OrderSelectLocker;
